import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { formatDateTime } from '../utils/dateFormat';
import { parseCNP, generateEpicrizaTemplate, generateRecomandariTemplate } from '../utils/cnpParser';
import {
  FileText,
  Upload,
  Calendar,
  Save,
  Link,
  ToggleLeft,
  ToggleRight,
  Image,
  Files,
  FileText as TemplateIcon,
} from 'lucide-react';
import DateTimePicker from '../components/DateTimePicker';
import DocumentUpload from '../components/DocumentUpload';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import FilePreview from '../components/FilePreview';
import FileSection from '../components/FileSection';
import BulkDeleteConfirmation from '../components/BulkDeleteConfirmation';
import { fadeInUp, staggerContainer } from '../utils/animations';

interface Profile {
  id: string;
  serviciu?: 'standard' | 'asociatie' | 'probono';
  prenume: string;
  nume: string;
  email: string;
  telefon?: string;
  cnp?: string;
  consultation_status: string;
  planned_date?: string;
  meeting_url?: string;
  drive_link?: string;
  notite?: string;
}

interface FileObject {
  name: string;
  id: string;
  created_at: string;
}

interface MedicalLetter {
  id?: string;
  diagnostic: string;
  epicriza: string;
  recomandari: string;
  notes4doctor: string;
  closed4editing?: 'closed';
}

interface MedicalHistoryEntry {
  id: string;
  doctor_id: string;
  changed_at: string;
  changes: {
    diagnostic?: string;
    epicriza?: string;
    recomandari?: string;
    notes4doctor?: string;
  };
  profiles: {
    prenume: string;
    nume: string;
  };
}

interface PreviewFile {
  url: string;
  name: string;
}

export default function PatientDocuments() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<Profile | null>(null);
  const [files, setFiles] = useState<FileObject[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [medicalLetterData, setMedicalLetterData] = useState<MedicalLetter>({
    diagnostic: '',
    epicriza: '',
    recomandari: '',
    notes4doctor: '',
    closed4editing: undefined,
  });
  const [savingLetter, setSavingLetter] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [generatingRecommendation, setGeneratingRecommendation] =
    useState(false);
  const [progress, setProgress] = useState(0);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [updatingService, setUpdatingService] = useState(false);
  const [currentDoctor, setCurrentDoctor] = useState<{
    prenume: string;
    nume: string;
    email: string;
  } | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedField, setSelectedField] = useState<string>('');
  const [fieldHistory, setFieldHistory] = useState<MedicalHistoryEntry[]>([]);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [editingNotite, setEditingNotite] = useState(false);
  const [currentNotite, setCurrentNotite] = useState('');
  const [savingNotite, setSavingNotite] = useState(false);

  const canGenerateRecommendation = Boolean(
    medicalLetterData.diagnostic && medicalLetterData.epicriza
  );

  const canPreviewFile = (fileName: string) => {
    return /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(fileName);
  };

  const getPreviewableFiles = () => {
    return files.filter((file) => canPreviewFile(file.name));
  };

  const getNonPreviewableFiles = () => {
    return files.filter((file) => !canPreviewFile(file.name));
  };

  // Function to handle textarea auto-resize
  const handleTextAreaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = `${target.scrollHeight}px`;
  };

  // Effect to adjust textareas on mount and when content changes
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    });
  }, [medicalLetterData]);

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role, prenume, nume, email')
        .eq('id', user.id)
        .single();

      if (!profile || profile.role !== 'doctor') {
        navigate('/');
        return;
      }

      setCurrentDoctor({
        prenume: profile.prenume,
        nume: profile.nume,
        email: profile.email,
      });

      await fetchPatientData();
    } catch (error) {
      console.error('Error checking auth:', error);
      navigate('/login');
    }
  };

  const fetchPatientData = async () => {
    if (!patientId) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', patientId)
        .single();

      if (error) throw error;
      setPatient(data);

      if (data.cnp) {
        await fetchFiles(data.cnp);
        await fetchMedicalLetter(patientId);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
      setError('Error fetching patient data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async (cnp: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('medical-documents')
        .list(cnp);

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchMedicalLetter = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('medical_letters')
        .select('*')
        .eq('patient_id', patientId)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error fetching medical letter:', error);
        }
        return;
      }

      if (data) {
        setMedicalLetterData({
          id: data.id,
          diagnostic: data.diagnostic || '',
          epicriza: data.epicriza || '',
          recomandari: data.recomandari || '',
          notes4doctor: data.notes4doctor || '',
          closed4editing: data.closed4editing,
        });
      }
    } catch (error) {
      console.error('Error fetching medical letter:', error);
    }
  };

  const fetchFieldHistory = async (fieldName: string) => {
    if (!medicalLetterData.id) return;

    try {
      const { data, error } = await supabase
        .from('medical_letter_history')
        .select(
          `
          id,
          doctor_id,
          changed_at,
          changes,
          profiles (
            prenume,
            nume
          )
        `
        )
        .eq('medical_letter_id', medicalLetterData.id)
        .order('changed_at', { ascending: false });

      if (error) throw error;

      // Filter entries that have changes for the selected field
      const relevantHistory = (data || [])
        .filter(
          (entry) =>
            entry.changes &&
            entry.changes[fieldName as keyof typeof entry.changes]
        )
        .map(entry => ({
          ...entry,
          profiles: Array.isArray(entry.profiles) 
            ? (entry.profiles[0] || { prenume: 'Unknown', nume: 'Doctor' })
            : (entry.profiles || { prenume: 'Unknown', nume: 'Doctor' })
        }));

      setFieldHistory(relevantHistory);
      setSelectedField(fieldName);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      setError('Error fetching field history');
    }
  };

  const handleGenerateUploadLink = async () => {
    if (!patient || generatingLink) return;
    setGeneratingLink(true);
    setError(null);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/link-drive',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: patient.prenume,
            nume: patient.nume,
            cnp: patient.cnp,
          }),
        }
      );

      const link = await response.text();

      if (link && link.startsWith('http')) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ drive_link: link })
          .eq('id', patient.id);

        if (updateError) throw updateError;

        await fetchPatientData();

        setSuccess('Link generat cu succes');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Link invalid primit de la server');
      }
    } catch (error) {
      console.error('Error generating upload link:', error);
      setError('Eroare la generarea link-ului. Vă rugăm încercați din nou.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handlePreview = async (fileName: string) => {
    if (!patient?.cnp) return;

    try {
      const { data, error } = await supabase.storage
        .from('medical-documents')
        .createSignedUrl(`${patient.cnp}/${fileName}`, 3600); // 1 hour expiry

      if (error) throw error;

      setPreviewFile({
        url: data.signedUrl,
        name: fileName,
      });
    } catch (error) {
      console.error('Error getting file URL:', error);
      setError('Error previewing file');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDownload = async (fileNames: string[]) => {
    if (!patient?.cnp) return;

    for (const fileName of fileNames) {
      try {
        const { data, error } = await supabase.storage
          .from('medical-documents')
          .download(`${patient.cnp}/${fileName}`);

        if (error) throw error;

        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading file:', error);
        setError('Eroare la descărcarea documentului');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleDelete = async (fileNames: string[]) => {
    if (!patient?.cnp) return;

    try {
      const filePaths = fileNames.map(
        (fileName) => `${patient.cnp}/${fileName}`
      );
      const { error } = await supabase.storage
        .from('medical-documents')
        .remove(filePaths);

      if (error) throw error;

      setSuccess('Documente șterse cu succes');
      setTimeout(() => setSuccess(null), 3000);
      await fetchFiles(patient.cnp);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error deleting files:', error);
      setError('Eroare la ștergerea documentelor');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleSelectFile = (fileName: string, selected: boolean) => {
    setSelectedFiles((prev) =>
      selected ? [...prev, fileName] : prev.filter((f) => f !== fileName)
    );
  };

  const handleSelectAllFiles = (selected: boolean, files: FileObject[]) => {
    setSelectedFiles(selected ? files.map((f) => f.name) : []);
  };

  const handleSaveAppointment = async () => {
    if (!selectedDate || !patient || savingAppointment) return;

    setSavingAppointment(true);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          planned_date: selectedDate,
          consultation_status: 'planned',
        })
        .eq('id', patient.id);

      if (updateError) throw updateError;

      const date = new Date(selectedDate);
      const formattedDate = date.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const formattedTime = date.toLocaleTimeString('ro-RO', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/Generare-google-meet',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: patient.prenume,
            nume: patient.nume,
            cnp: patient.cnp,
            telefon: patient.telefon || '',
            email: patient.email,
            consultationdate: formattedDate,
            consultationtime: formattedTime,
          }),
        }
      );

      const result = await response.text();
      if (result) {
        await supabase
          .from('profiles')
          .update({ meeting_url: result })
          .eq('id', patient.id);
      }

      await fetchPatientData();
      setSuccess('Programare salvată cu succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error scheduling patient:', error);
      setError('Eroare la programarea pacientului');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleGenerateRecommendation = async () => {
    if (!medicalLetterData.diagnostic || !medicalLetterData.epicriza) {
      return;
    }

    setGeneratingRecommendation(true);
    setProgress(0);

    const startTime = Date.now();
    const duration = 120000;

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (elapsed >= duration) {
        clearInterval(progressInterval);
      }
    }, 50);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/generare-recomandare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            diagnostic: medicalLetterData.diagnostic,
            epicriza: medicalLetterData.epicriza,
            recomandare: medicalLetterData.recomandari,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate recommendation');
      }

      const recommendation = await response.text();
      setMedicalLetterData((prev) => ({
        ...prev,
        recomandari: prev.recomandari ? prev.recomandari + '\n' + recommendation : recommendation,
      }));
    } catch (error) {
      console.error('Error generating recommendation:', error);
      setError('Error generating recommendation');
    } finally {
      clearInterval(progressInterval);
      setGeneratingRecommendation(false);
      setProgress(0);
    }
  };

  const handleSaveMedicalLetter = async () => {
    if (!patientId) return;

    setSavingLetter(true);
    try {
      // First, save the medical letter
      const { data: letterData, error: letterError } = await supabase
        .from('medical_letters')
        .upsert(
          {
            patient_id: patientId,
            diagnostic: medicalLetterData.diagnostic,
            epicriza: medicalLetterData.epicriza,
            recomandari: medicalLetterData.recomandari,
            notes4doctor: medicalLetterData.notes4doctor,
          },
          {
            onConflict: 'patient_id',
          }
        )
        .select()
        .single();

      if (letterError) throw letterError;

      // Then, save the history entry
      const { error: historyError } = await supabase
        .from('medical_letter_history')
        .insert({
          medical_letter_id: letterData.id,
          doctor_id: (await supabase.auth.getUser()).data.user?.id,
          changes: {
            diagnostic: medicalLetterData.diagnostic,
            epicriza: medicalLetterData.epicriza,
            recomandari: medicalLetterData.recomandari,
            notes4doctor: medicalLetterData.notes4doctor,
          },
        });

      if (historyError) throw historyError;

      setSuccess('Scrisoarea medicală a fost salvată');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving medical letter:', error);
      setError('Error saving medical letter');
    } finally {
      setSavingLetter(false);
    }
  };

  const handleServiceToggle = async () => {
    if (!patient || updatingService || !currentDoctor) return;
    setUpdatingService(true);

    try {
      const newService =
        patient.serviciu === 'probono' ? 'standard' : 'probono';

      const { error } = await supabase
        .from('profiles')
        .update({ serviciu: newService })
        .eq('id', patient.id);

      if (error) throw error;

      if (newService === 'probono') {
        const response = await fetch(
          'https://n8n.ovi.net.eu.org/webhook/setare-probono',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              patient: {
                prenume: patient.prenume,
                nume: patient.nume,
                email: patient.email,
                cnp: patient.cnp,
                telefon: patient.telefon || '',
              },
              doctor: {
                prenume: currentDoctor.prenume,
                nume: currentDoctor.nume,
                email: currentDoctor.email,
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to send webhook');
        }
      }

      await fetchPatientData();

      setSuccess(`Serviciu actualizat la ${newService}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Eroare la actualizarea serviciului');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUpdatingService(false);
    }
  };

  const handleGenerateEpicrizaTemplate = () => {
    if (!patient?.cnp) {
      setError('Pacientul nu are CNP completat');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const cnpData = parseCNP(patient.cnp);
    if (!cnpData.isValid) {
      setError('CNP-ul pacientului nu este valid');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const template = generateEpicrizaTemplate(cnpData);
    setMedicalLetterData((prev) => ({
      ...prev,
      epicriza: prev.epicriza ? prev.epicriza + '\n' + template : template,
    }));

    setSuccess('Șablon epicriză generat cu succes');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleGenerateRecomandariTemplate = () => {
    if (!patient?.cnp) {
      setError('Pacientul nu are CNP completat');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const cnpData = parseCNP(patient.cnp);
    if (!cnpData.isValid) {
      setError('CNP-ul pacientului nu este valid');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const template = generateRecomandariTemplate(cnpData);
    setMedicalLetterData((prev) => ({
      ...prev,
      recomandari: prev.recomandari ? prev.recomandari + '\n' + template : template,
    }));

    setSuccess('Șablon recomandări generat cu succes');
    setTimeout(() => setSuccess(null), 3000);
  };

  const [unlockingLetter, setUnlockingLetter] = useState(false);

  const handleUnlockLetter = async () => {
    if (!patientId || !patient) return;

    setUnlockingLetter(true);
    try {
      // Update patient consultation_status to 'planned'
      const { error: patientError } = await supabase
        .from('profiles')
        .update({ consultation_status: 'planned' })
        .eq('id', patient.id);

      if (patientError) throw patientError;

      // Update medical_letter closed4editing to NULL
      if (medicalLetterData.id) {
        const { error: letterError } = await supabase
          .from('medical_letters')
          .update({ closed4editing: null })
          .eq('id', medicalLetterData.id);

        if (letterError) throw letterError;
      }

      // Refresh data
      await fetchPatientData();
      await fetchMedicalLetter(patientId);

      setSuccess('Scrisoarea a fost deblocată cu succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error unlocking letter:', error);
      setError('Eroare la deblocarea scrisorii');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUnlockingLetter(false);
    }
  };

  // Handle content restoration from history
  const handleRestoreContent = (fieldName: string, content: string) => {
    setMedicalLetterData((prev) => ({
      ...prev,
      [fieldName]: content,
    }));
    setShowHistoryModal(false);
    setSuccess('Conținut restaurat cu succes');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Check if current user is admin
  const isAdminUser = currentDoctor && 
    (currentDoctor.email === 'ovidiu.mudura@yahoo.com' || 
     currentDoctor.email === 'tudorbolos2001@yahoo.com');

  // Function to handle saving notes
  const handleSaveNotite = async () => {
    if (!patient || savingNotite) return;
    
    setSavingNotite(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notite: currentNotite })
        .eq('id', patient.id);
      
      if (error) throw error;
      
      // Update local state
      setPatient(prev => prev ? { ...prev, notite: currentNotite } : null);
      setEditingNotite(false);
      
      setSuccess('Notițele au fost salvate cu succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving notes:', error);
      setError('Eroare la salvarea notițelor');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSavingNotite(false);
    }
  };

  // Function to start editing notes
  const handleStartEditNotite = () => {
    setCurrentNotite(patient?.notite || '');
    setEditingNotite(true);
  };

  // Function to cancel editing
  const handleCancelEditNotite = () => {
    setEditingNotite(false);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Se încarcă...</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">Pacientul nu a fost găsit</div>
      </div>
    );
  }

  const previewableFiles = getPreviewableFiles();
  const otherFiles = getNonPreviewableFiles();

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-8 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Patient Info Section */}
          <motion.div
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
            variants={fadeInUp}
          >
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Dosar pacient: {patient.prenume} {patient.nume}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600">CNP: {patient.cnp}</p>
                <p className="text-gray-600">Email: {patient.email}</p>
                <p className="text-gray-600">
                  Telefon: {patient.telefon || '-'}
                </p>
                <p className="text-gray-600">
                  Serviciu: {patient.serviciu === 'standard' ? 'Standard' : patient.serviciu === 'asociatie' ? 'Asociație' : patient.serviciu === 'probono' ? 'Pro Bono' : '-'}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-sm font-medium ${
                      patient.consultation_status === 'not_requested'
                        ? 'bg-gray-100 text-gray-800'
                        : patient.consultation_status === 'requested'
                        ? 'bg-yellow-100 text-yellow-800'
                        : patient.consultation_status === 'planned'
                        ? 'bg-blue-100 text-blue-800'
                        : patient.consultation_status === 'closed'
                        ? 'bg-green-100 text-green-800'
                        : patient.consultation_status === 'paid'
                        ? 'bg-purple-100 text-purple-800'
                        : patient.consultation_status === 'contacted'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {patient.consultation_status === 'not_requested'
                      ? 'Fără cerere'
                      : patient.consultation_status === 'requested'
                      ? 'Cerere în așteptare'
                      : patient.consultation_status === 'planned'
                      ? 'Programat'
                      : patient.consultation_status === 'closed'
                      ? 'Închis'
                      : patient.consultation_status === 'paid'
                      ? 'Plătit'
                      : patient.consultation_status === 'contacted'
                      ? 'Contactat'
                      : 'Necunoscut'}
                  </span>
                </div>
                {(patient.consultation_status === 'not_requested' ||
                  patient.consultation_status === 'contacted') && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Pro Bono:</span>
                    <button
                      onClick={handleServiceToggle}
                      disabled={updatingService}
                      className="text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {patient.serviciu === 'probono' ? (
                        <ToggleRight className="w-6 h-6 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-6 h-6" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Documents Section */}
          <motion.div
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
            variants={fadeInUp}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Documente Medicale
              </h2>
              <div className="flex gap-2">
                <div>
                  {patient?.drive_link ? (
                    <a
                      href={patient.drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      <Link className="w-4 h-4" />
                      Accesează link Drive
                    </a>
                  ) : (
                    <button
                      onClick={handleGenerateUploadLink}
                      disabled={generatingLink}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      <Link className="w-4 h-4" />
                      {generatingLink
                        ? 'Se generează...'
                        : 'Generează link Drive'}
                    </button>
                  )}
                  <div className="mt-2 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <p className="text-yellow-800 text-sm">
                      Documentele din drive se vor șterge după generarea scrisorii medicale din cauza lipsei de spațiu de stocare
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition"
                >
                  <Upload className="w-4 h-4" />
                  Încarcă documente
                </button>
              </div>
            </div>

            <div className="space-y-8">
              {/* Previewable Files Section */}
              <FileSection
                title="Documente cu previzualizare"
                icon="image"
                files={previewableFiles}
                selectedFiles={selectedFiles}
                onSelect={handleSelectFile}
                onSelectAll={(selected) =>
                  handleSelectAllFiles(selected, previewableFiles)
                }
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={(files) => setShowDeleteConfirmation(true)}
                canPreview={canPreviewFile}
              />

              {/* Other Files Section */}
              <FileSection
                title="Alte documente"
                icon="files"
                files={otherFiles}
                selectedFiles={selectedFiles}
                onSelect={handleSelectFile}
                onSelectAll={(selected) =>
                  handleSelectAllFiles(selected, otherFiles)
                }
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={(files) => setShowDeleteConfirmation(true)}
                canPreview={canPreviewFile}
              />
            </div>
          </motion.div>

          {/* Medical Letter Section */}
          <motion.div
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
            variants={fadeInUp}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Scrisoare Medicală
            </h2>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Diagnostic
                  </label>
                  <HistoryButton
                    onClick={() => fetchFieldHistory('diagnostic')}
                  />
                </div>
                <textarea
                  value={medicalLetterData.diagnostic}
                  onChange={(e) => {
                    handleTextAreaInput(e);
                    setMedicalLetterData((prev) => ({
                      ...prev,
                      diagnostic: e.target.value,
                    }));
                  }}
                  onInput={handleTextAreaInput}
                  disabled={medicalLetterData.closed4editing === 'closed'}
                  className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[100px] ${
                    medicalLetterData.closed4editing === 'closed'
                      ? 'bg-gray-100 text-gray-700 cursor-not-allowed'
                      : ''
                  }`}
                  style={{ resize: 'none', overflow: 'hidden' }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Epicriză
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleGenerateEpicrizaTemplate}
                      disabled={medicalLetterData.closed4editing === 'closed' || !patient?.cnp}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <TemplateIcon className="w-3 h-3" />
                      Șablon epicriză
                    </button>
                    <HistoryButton
                      onClick={() => fetchFieldHistory('epicriza')}
                    />
                  </div>
                </div>
                <textarea
                  value={medicalLetterData.epicriza}
                  onChange={(e) => {
                    handleTextAreaInput(e);
                    setMedicalLetterData((prev) => ({
                      ...prev,
                      epicriza: e.target.value,
                    }));
                  }}
                  onInput={handleTextAreaInput}
                  disabled={medicalLetterData.closed4editing === 'closed'}
                  className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[150px] ${
                    medicalLetterData.closed4editing === 'closed'
                      ? 'bg-gray-100 text-gray-700 cursor-not-allowed'
                      : ''
                  }`}
                  style={{ resize: 'none', overflow: 'hidden' }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Recomandări
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleGenerateRecomandariTemplate}
                      disabled={medicalLetterData.closed4editing === 'closed' || !patient?.cnp}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <TemplateIcon className="w-3 h-3" />
                      Șablon recomandări
                    </button>
                    <HistoryButton
                      onClick={() => fetchFieldHistory('recomandari')}
                    />
                  </div>
                </div>
                <textarea
                  value={medicalLetterData.recomandari}
                  onChange={(e) => {
                    handleTextAreaInput(e);
                    setMedicalLetterData((prev) => ({
                      ...prev,
                      recomandari: e.target.value,
                    }));
                  }}
                  onInput={handleTextAreaInput}
                  disabled={medicalLetterData.closed4editing === 'closed'}
                  className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[100px] ${
                    medicalLetterData.closed4editing === 'closed'
                      ? 'bg-gray-100 text-gray-700 cursor-not-allowed'
                      : ''
                  }`}
                  style={{ resize: 'none', overflow: 'hidden' }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Note pentru medic vizibile și pacientului
                  </label>
                  <HistoryButton
                    onClick={() => fetchFieldHistory('notes4doctor')}
                  />
                </div>
                <textarea
                  value={medicalLetterData.notes4doctor}
                  onChange={(e) => {
                    handleTextAreaInput(e);

                    setMedicalLetterData((prev) => ({
                      ...prev,
                      notes4doctor: e.target.value,
                    }));
                  }}
                  onInput={handleTextAreaInput}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[100px]"
                  style={{ resize: 'none', overflow: 'hidden' }}
                  placeholder="Aici apar note transmise de către pacient"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Notițe vizibile doar pentru medic
                  </label>
                  {!editingNotite && (
                    <button
                      onClick={handleStartEditNotite}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition"
                    >
                      <FileText className="w-3 h-3" />
                      Editează notițe
                    </button>
                  )}
                </div>
                {editingNotite ? (
                  <div className="space-y-2">
                    <textarea
                      value={currentNotite}
                      onChange={(e) => {
                        handleTextAreaInput(e);
                        setCurrentNotite(e.target.value);
                      }}
                      onInput={handleTextAreaInput}
                      className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[100px]"
                      style={{ resize: 'none', overflow: 'hidden' }}
                      placeholder="Adaugă notițe vizibile doar pentru medic..."
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveNotite}
                        disabled={savingNotite}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50"
                      >
                        <Save className="w-3 h-3" />
                        {savingNotite ? 'Se salvează...' : 'Salvează notițe'}
                      </button>
                      <button
                        onClick={handleCancelEditNotite}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
                      >
                        Anulează
                      </button>
                    </div>
                  </div>
                ) : patient.notite ? (
                  <div className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 min-h-[100px] whitespace-pre-wrap">
                    {patient.notite}
                  </div>
                ) : (
                  <div className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 min-h-[100px] text-gray-500 italic">
                    Nu există notițe adăugate de către medic.
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleGenerateRecommendation}
                      disabled={
                        !canGenerateRecommendation ||
                        generatingRecommendation ||
                        medicalLetterData.closed4editing === 'closed'
                      }
                      onMouseEnter={() =>
                        !canGenerateRecommendation && setShowTooltip(true)
                      }
                      onMouseLeave={() => setShowTooltip(false)}
                      className={`px-4 py-2 text-white rounded-lg transition ${
                        canGenerateRecommendation &&
                        !medicalLetterData.closed4editing
                          ? 'bg-[#007FFF] hover:bg-[#024994]'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {generatingRecommendation
                        ? 'Se generează...'
                        : 'Generează recomandare'}
                    </button>

                    {generatingRecommendation && (
                      <div className="relative inline-flex items-center justify-center">
                        <svg className="transform -rotate-90 w-12 h-12">
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            className="text-gray-200"
                          />
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            className="text-[#007FFF]"
                            strokeDasharray={`${2 * Math.PI * 20}`}
                            strokeDashoffset={`${
                              2 * Math.PI * 20 * (1 - progress / 100)
                            }`}
                            strokeLinecap="round"
                            style={{
                              transition: 'stroke-dashoffset 0.1s ease',
                            }}
                          />
                        </svg>
                        <span className="absolute text-xs font-medium text-[#007FFF]">
                          {Math.round(progress)}%
                        </span>
                      </div>
                    )}
                  </div>

                  {showTooltip && !canGenerateRecommendation && (
                    <div className="absolute left-1/2 bottom-full transform -translate-x-1/2 mb-2">
                      <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                        Pentru a genera recomandări, completați câmpurile
                        Diagnostic și Epicriză
                      </div>
                    </div>
                  )}
                </div>

                {isAdminUser && (
                  <button
                    onClick={handleUnlockLetter}
                    disabled={unlockingLetter || medicalLetterData.closed4editing !== 'closed'}
                    className={`px-4 py-2 text-white rounded-lg transition ${
                      medicalLetterData.closed4editing === 'closed'
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {unlockingLetter
                      ? 'Se deblochează...'
                      : 'Deblocare scrisoare'}
                  </button>
                )}
                <button
                  onClick={handleSaveMedicalLetter}
                  disabled={
                    savingLetter ||
                    medicalLetterData.closed4editing === 'closed'
                  }
                  className={`px-4 py-2 text-white rounded-lg transition ${
                    medicalLetterData.closed4editing === 'closed'
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#007FFF] hover:bg-[#024994]'
                  }`}
                >
                  {savingLetter
                    ? 'Se salvează...'
                    : 'Salvează scrisoarea medicală'}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Appointment Section - Only show for requested/paid/probono */}
          {(patient.consultation_status === 'requested' ||
            patient.consultation_status === 'paid' ||
            patient.serviciu === 'probono') && (
            <motion.div
              className="bg-white rounded-lg shadow-lg p-6 min-h-[384px]"
              variants={fadeInUp}
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Programează pacient
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data și ora consultației
                  </label>
                  <div className="space-y-4">
                    <DateTimePicker
                      value={
                        selectedDate ||
                        patient?.planned_date ||
                        new Date().toISOString()
                      }
                      onChange={(value) => setSelectedDate(value)}
                      min={new Date().toISOString()}
                      className="w-full"
                    />
                    <button
                      onClick={handleSaveAppointment}
                      disabled={savingAppointment || !selectedDate}
                      className="flex items-center gap-2 px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition disabled:opacity-50"
                    >
                      <Calendar className="w-4 h-4" />
                      {savingAppointment
                        ? 'Se salvează...'
                        : 'Salvează programarea'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {showUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <DocumentUpload
              onClose={() => {
                setShowUpload(false);
                if (patient?.cnp) {
                  fetchFiles(patient.cnp);
                }
              }}
              patientCnp={patient?.cnp}
            />
          </motion.div>
        )}

        {showDeleteConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <BulkDeleteConfirmation
              isOpen={showDeleteConfirmation}
              onClose={() => setShowDeleteConfirmation(false)}
              onConfirm={() => handleDelete(selectedFiles)}
              fileCount={selectedFiles.length}
            />
          </motion.div>
        )}

        {showHistoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <HistoryModal
              isOpen={showHistoryModal}
              onClose={() => setShowHistoryModal(false)}
              history={fieldHistory}
              fieldName={selectedField}
              onRestore={handleRestoreContent}
            />
          </motion.div>
        )}

        {previewFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FilePreview
              url={previewFile.url}
              fileName={previewFile.name}
              onClose={() => setPreviewFile(null)}
            />
          </motion.div>
        )}
        {error && (
          <motion.div
            className="fixed bottom-4 right-4 p-4 bg-red-100 text-red-800 rounded-lg shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            className="fixed bottom-4 right-4 p-4 bg-green-100 text-green-800 rounded-lg shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {success}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
